<template>
  <main class="main main--has-floating-buttons">
    <template v-if="dataLoaded === true">
      <portal to="headerTitle">
        {{ tax.name[hotelSettings.details.primaryLanguage] }}
      </portal>

      <tabable-card cols="12 6@desktop">
        <tabable-card-content :title="$t(`lang.${hotelSettings.details.primaryLanguage}`)">
          <hm-card-inner>
            <hm-input v-model="tax.name[hotelSettings.details.primaryLanguage]"
                      :lazy="true"
                      :disabled="tax.deleted"
                      :label="$t('admin.pageTax.title')"
                      validate="required"
                      name="title"
                      cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>
      <tabable-card cols="12 6@desktop">
        <tabable-card-content v-for="lang in hotelSettings.details.additionalLanguages" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner>
            <hm-input v-model="tax.name[lang]"
                      :lazy="true"
                      :disabled="tax.deleted"
                      :label="$t('admin.pageTax.title')"
                      name="title"
                      cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <card :title="$t('admin.pageTax.card.general')" cols="12">
        <hm-card-inner :full-size="true">
          <hm-col v-if="tax.taxType === 'VAT'" cols="12">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.pageTax.hintVatLocked') }}</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select id="type"
                           v-model="tax.taxType"
                           :disabled="tax.deleted || (tax.taxType === 'VAT' && !hasRole('ROLE_SYSADMIN')) || (usedAnywhere && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                           :label="$t('admin.pageTax.type')"
                           :disabled-options="hasRole('ROLE_SYSADMIN') ? [] : ['VAT']"
                           name="type"
                           enum-name="TaxType"
                           cols="12 6@tablet 3@desktop" />
          <hm-input v-if="tax.taxType === 'VAT' && hotelSettings.details.vatIncluded !== false"
                    v-model="tax.vatLabel"
                    :disabled="tax.deleted"
                    :maxlength="3"
                    :label="$t('admin.pageTax.vatLabel')"
                    validate="required|min_length:1|max_length:3"
                    cols="12 6@tablet 3@desktop" />
          <hm-input-toggle v-if="tax.taxType !== 'VAT'"
                           v-model="tax.inclusiveTax"
                           :disabled="tax.deleted"
                           :label="$t('admin.pageTax.inclusiveTax')"
                           :enabled-title="$t('admin.pageTax.inclusiveTaxEnabled')"
                           :disabled-title="$t('admin.pageTax.inclusiveTaxDisabled')"
                           name="inclusiveTax"
                           cols="12 6@tablet 3@desktop" />
          <hm-input-money v-model="tax.amount"
                          :disabled="tax.deleted || (tax.taxType === 'VAT' && !hasRole('ROLE_SYSADMIN'))"
                          :currency="`${tax.percentage ? '%' : hotelSettings.details.currency}`"
                          :digits="tax.percentage ? 6 : 2"
                          :lazy="true"
                          :currency-clickable="tax.taxType !== 'VAT' && (!usedAnywhere || hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                          :icons-right="tax.percentage ? 0 : 2"
                          :label="$t('admin.pageTax.amount')"
                          name="amount"
                          cols="12 6@tablet 3@desktop start-1"
                          @currency-click="onCurrencyClick">
            <template v-if="!tax.percentage" slot="icon-inline-right">
              <input-icon-button v-if="tax.rateMode === 'PerRoom'"
                                 :border="true"
                                 :title="$t(`RateMode.${tax.rateMode}`)"
                                 :disabled="tax.deleted || (usedAnywhere && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                                 icon="per-room"
                                 @click="tax.rateMode = 'PerPerson'" />
              <input-icon-button v-else-if="tax.rateMode === 'PerPerson'"
                                 :border="true"
                                 :title="$t(`RateMode.${tax.rateMode}`)"
                                 :disabled="tax.deleted || (usedAnywhere && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                                 icon="per-person"
                                 @click="tax.rateMode = 'PerRoom'" />
              <input-icon-button v-if="tax.rateFrequency === 'PerStay'"
                                 :border="true"
                                 :title="$t(`RateFrequency.${tax.rateFrequency}`)"
                                 :disabled="tax.deleted || (usedAnywhere && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                                 icon="per-stay"
                                 @click="tax.rateFrequency = 'PerNight'" />
              <input-icon-button v-else-if="tax.rateFrequency === 'PerNight'"
                                 :border="true"
                                 :title="$t(`RateFrequency.${tax.rateFrequency}`)"
                                 :disabled="tax.deleted || (usedAnywhere && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                                 icon="per-night"
                                 @click="tax.rateFrequency = 'PerStay'" />
            </template>
          </hm-input-money>
          <hm-multiselect v-if="!tax.percentage && tax.rateMode === 'PerPerson'"
                          id="ageCategories"
                          v-model="tax.ageCategories"
                          :options="hotelSettings.ageCategories.all(tax.ageCategories).filter(it => !it.deleted)"
                          :disabled="tax.deleted"
                          :label="$t('admin.pageTax.ageCategories')"
                          cols="12 6@tablet 3@desktop"
                          name="ageCategories"
                          label-expression="${option.name[$shortLocale]}" />

          <hm-input v-if="hasAnyLicense(['LedgerReport', 'DatevReport'])"
                    id="ledgerCode"
                    v-model="tax.ledgerCode"
                    :disabled="tax.deleted"
                    :label="$t('admin.pageTax.ledgerCode')"
                    cols="12 6@tablet 3@desktop start-1"
                    name="ledgerCode" />
        </hm-card-inner>

        <hm-card-inner v-if="hasLicense('FiskaltrustAgencyBusiness')">
          <hm-input v-if="hasLicense('FiskaltrustAgencyBusiness')"
                    id="fiscalizationAgencyId"
                    v-model="tax.fiscalizationAgencyId"
                    :disabled="tax.deleted || !hasRole('ROLE_SYSADMIN')"
                    :label="$t('admin.pageTax.fiscalizationAgencyId')"
                    cols="12 6@tablet 3@desktop start-1"
                    name="fiscalizationAgencyId" />
        </hm-card-inner>
      </card>

      <card :title="$t('admin.pageTax.card.validityDateRanges')" cols="12">
        <hm-card-inner v-if="isEmpty(tax.validityDateRanges) && (!isEmpty(tax.validFrom) || !isEmpty(tax.validUntil))" :title="$t('admin.pageTax.card.validityStartEndDates')">
          <hm-input-date v-model="tax.validFrom"
                         :required="false"
                         :disabled="tax.deleted"
                         :label="$t('admin.pageTax.validFrom')"
                         max-date="20991231"
                         cols="12 6@tablet 3@desktop"
                         name="validFrom" />
          <hm-input-date v-model="tax.validUntil"
                         :min-date="tax.validFrom"
                         :disabled="tax.deleted"
                         :max-days="36500"
                         :required="false"
                         :label="$t('admin.pageTax.validUntil')"
                         cols="12 6@tablet 3@desktop"
                         name="validUntil" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageTax.card.seasonalValidity')">
          <hm-input-mask v-model="tax.seasonalValidFrom"
                         :disabled="tax.deleted"
                         :label="$t('admin.pageTax.seasonalValidFrom')"
                         :help="$t('admin.pageTax.seasonalValidFrom_help', {format: dayMonthFormat, example: $moment('23-12', 'DD-MM').format(dayMonthFormat)})"
                         :mask="dayMonthMask"
                         type="text"
                         cols="12 6@tablet 3@desktop"
                         name="seasonalValidFrom" />
          <hm-input-mask v-model="tax.seasonalValidUntil"
                         :disabled="tax.deleted"
                         :label="$t('admin.pageTax.seasonalValidUntil')"
                         :help="$t('admin.pageTax.seasonalValidUntil_help', {format: dayMonthFormat, example: $moment('23-12', 'DD-MM').format(dayMonthFormat)})"
                         :mask="dayMonthMask"
                         type="text"
                         cols="12 6@tablet 3@desktop"
                         name="seasonalValidUntil" />
        </hm-card-inner>
      </card>

      <card v-if="!isEmpty(tax.validityDateRanges)" :title="$t('admin.pageTax.card.validityDateRangesMultiple')" cols="12">
        <hm-card-inner v-for="dateRange in tax.validityDateRanges" :key="dateRange.id" :deletable="true" @delete="deleteValidityDateRange(dateRange)">
          <hm-input-date v-model="dateRange.startDate"
                         :required="false"
                         :disabled="tax.deleted"
                         :label="$t('admin.pageTax.startDate')"
                         max-date="20991231"
                         cols="12 4@tablet 3@desktop"
                         name="startDate" />
          <hm-input-date v-model="dateRange.endDate"
                         :min-date="dateRange.startDate"
                         :disabled="tax.deleted"
                         :max-days="36500"
                         :required="false"
                         :label="$t('admin.pageTax.endDate')"
                         cols="12 4@tablet 3@desktop"
                         name="endDate" />
        </hm-card-inner>
      </card>

      <card v-if="!canDelete" :title="$t('admin.pageTax.card.usage')" cols="12" card-type="collapsible">
        <hm-card-inner v-if="!isEmpty(usedRatePlans)" :full-size="true" :title="$t('admin.pageTax.card.usageRatePlans')">
          <hm-col cols="12">
            <template v-for="it in usedRatePlans" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToRatePlan(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="!isEmpty(usedInHouseServices)" :full-size="true" :title="$t('admin.pageTax.card.usageInHouseServices')">
          <hm-col cols="12">
            <template v-for="it in usedInHouseServices" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToInHouseService(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="!isEmpty(usedMealPlans)" :full-size="true" :title="$t('admin.pageTax.card.usageInMealPlans')">
          <hm-col cols="12">
            <template v-for="it in usedMealPlans" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToMealPlan(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
      </card>

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!tax.deleted"
                            :label="$t('admin.pageTax.actions.addValidityPeriod')"
                            icon="plus"
                            color="blue"
                            @click="addValidityDateRange()" />
        <hm-floating-button v-if="!tax.deleted && canDelete && ((hasRole('ROLE_SYSADMIN') && tax.taxType !== 'VAT') || hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN'))"
                            :label="$t('admin.pageTax.actions.deleteTax')"
                            icon="delete"
                            color="error"
                            @click="deleteTax(false)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import uuid from 'uuid';
import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, keyBy, sortBy, size } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import HmInputDate from '../../lib/inputs/HmInputDate';
import HmInputSelect from '../../lib/inputs/HmInputSelect.vue';

export default {
  name: 'PageTax',
  components: { HmInputSelect, HmInputDate, HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'tax',
      variable: 'tax',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.tax.deleted === true) {
          cbSaveComplete();
          return;
        }

        const toSave = cloneDeep(this.tax);

        if (isEmpty(toSave.seasonalValidFrom) && isEmpty(toSave.seasonalValidUntil)) {
          toSave.seasonalValidFrom = null;
          toSave.seasonalValidUntil = null;
        }

        if (isEmpty(toSave.seasonalValidFrom) && !isEmpty(toSave.seasonalValidUntil)) {
          toSave.seasonalValidFrom = this.$moment('01-01', 'DD-MM').format(this.dayMonthFormat);
        }

        if (!isEmpty(toSave.seasonalValidFrom) && isEmpty(toSave.seasonalValidUntil)) {
          toSave.seasonalValidUntil = this.$moment('31-12', 'DD-MM').format(this.dayMonthFormat);
        }

        if (!isEmpty(toSave.seasonalValidFrom) && !isEmpty(toSave.seasonalValidUntil)) {
          if (toSave.seasonalValidFrom.length !== 6 || toSave.seasonalValidUntil.length !== 6) {
            toSave.seasonalValidFrom = null;
            toSave.seasonalValidUntil = null;
          } else {
            toSave.seasonalValidFrom = this.$moment(toSave.seasonalValidFrom, this.dayMonthFormat).format('DD-MM').split(/[^0-9]+/g);
            toSave.seasonalValidUntil = this.$moment(toSave.seasonalValidUntil, this.dayMonthFormat).format('DD-MM').split(/[^0-9]+/g);
          }
        }

        if (toSave.taxType === 'VAT' && this.hotelSettings.details.vatIncluded !== false && size(toSave.vatLabel) === 0) {
          cbSaveComplete();
          return;
        }

        this.axios.put(this.serviceUrls.tax.get(this, this.taxId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();

            this.$serviceBus.$emit('tax.list');
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      tax: {
        taxType: null,
        rateMode: null,
        rateFrequency: null,
        validityDateRanges: null,
      },
      taxId: this.$route.params.taxId,
      dataLoaded: false,
    };
  },

  computed: {
    usedRatePlans: function () {
      return this.hotelSettings.ratePlans
        .filter(it => !it.deleted && !isNil(it.applicableTaxes)
          && it.applicableTaxes.filter(tax => tax.id === this.tax.id).length > 0);
    },
    usedInHouseServices: function () {
      return this.hotelSettings.inHouseServices
        .filter(it => !it.deleted && !isNil(it.applicableTaxes)
          && it.applicableTaxes.filter(tax => tax.id === this.tax.id).length > 0);
    },
    usedMealPlans: function () {
      return this.hotelSettings.mealPlans
        .filter(it => !it.deleted && !isNil(it.applicableTaxes)
          && it.applicableTaxes.filter(tax => tax.id === this.tax.id).length > 0);
    },
    usedAnywhere: function () {
      return this.usedRatePlans.length > 0 || this.usedInHouseServices.length > 0 || this.usedMealPlans.length > 0;
    },
    canDelete: function () {
      return this.usedRatePlans.length === 0 && this.usedInHouseServices.length === 0 && this.usedMealPlans.length === 0;
    },
  },

  watch: {
    'tax.taxType': function (newVal) {
      if (newVal === 'VAT') {
        this.tax.percentage = true;
        this.tax.inclusiveTax = isNil(this.hotelSettings.details.vatIncluded) ? true : this.hotelSettings.details.vatIncluded;
      } else {
        this.tax.vatLabel = null;
      }
    },
    tax: function (newVal) {
      if (isNil(newVal)) {
        this.dataLoaded = false;
      }
    },
  },

  created: function () {
    this.loadData();
    this.$serviceBus.$emit('tax.list');
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    addValidityDateRange() {
      if (this.tax.validityDateRanges == null) {
        this.tax.validityDateRanges = [];
      }
      this.tax.validityDateRanges.push({
        id: uuid.v4(),
        startDate: null,
        endDate: null,
      });
    },
    deleteValidityDateRange(dateRange) {
      const idx = this.tax.validityDateRanges.indexOf(dateRange);
      if (idx >= 0) {
        this.tax.validityDateRanges.splice(idx, 1);
      }
    },
    routeToRatePlan(ratePlan) {
      this.$router.push({
        name: 'ratePlan',
        params: {
          ratePlanId: ratePlan.id,
        },
      });
    },
    routeToRoomType(roomType) {
      this.$router.push({
        name: 'roomType',
        params: {
          roomTypeId: roomType.id,
        },
      });
    },
    routeToMealPlan(mealPlan) {
      this.$router.push({
        name: 'mealPlan',
        params: {
          mealPlanId: mealPlan.id,
        },
      });
    },
    routeToInHouseService(inHouseService) {
      this.$router.push({
        name: 'inHouseService',
        params: {
          inHouseServiceId: inHouseService.id,
        },
      });
    },
    loadData() {
      this.axios.get(this.serviceUrls.tax.get(this, this.taxId))
        .then((response) => {
          if (this.dataLoaded === false) {
            const tax = response.data.data;
            if (!isNil(tax.seasonalValidFrom)) {
              tax.seasonalValidFrom = this.$moment(`${tax.seasonalValidFrom[0]}-${tax.seasonalValidFrom[1]}`, 'DD-MM').format(this.dayMonthFormat);
            }
            if (!isNil(tax.seasonalValidUntil)) {
              tax.seasonalValidUntil = this.$moment(`${tax.seasonalValidUntil[0]}-${tax.seasonalValidUntil[1]}`, 'DD-MM').format(this.dayMonthFormat);
            }
            this.tax = tax;
            this.dataLoaded = true;
          }
        });
    },
    deleteTax(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.tax.get(this, this.taxId))
            .then((response) => {
              this.dataLoaded = false;
              this.tax = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('tax.list');
              this.$router.push({
                name: 'taxes',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('admin.pageTax.confirmDeleteTax'),
            (doNotShowAgain) => {
              this.deleteTax(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pageTax.taxInUse'));
      }
    },
    onCurrencyClick() {
      this.tax.percentage = !this.tax.percentage;
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageTax.vue